import { useQueryClient } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { useSubmitModuleSubmission } from '../../../api/endpoints/submissions/submissions';
import { getGetModulesQueryKey } from '../../../api/endpoints/modules/modules';
import { useAppStateStore, type ModuleSelection } from '../../../context/AppStateStore';
import { useDialog } from '../../../context/DialogContext';
import { useApiErrorReporter } from '../../../utils/apiErrorReporter';
import type { GenericApiResponseListModule, HTTPValidationError } from '../../../api/models';

/**
 * Hook to submit a module for evaluation.
 * Handles optimistic updates and error cases for the submission process.
 *
 * IDs are typed as UUID to enforce validation at boundaries.
 * If you have a ModuleSelection, the IDs are guaranteed to be valid UUIDs.
 *
 * @param params - Optional module selection. If not provided, uses the currently selected module from AppStateStore
 * @returns Object containing mutation state and a submit function
 *
 * @example
 * ```typescript
 * const { submit, isLoading, isError, error } = useSubmitModule({ moduleId, courseId });
 *
 * // Submit the module
 * submit();
 * ```
 */
export const useSubmitModule = (params?: NonNullable<ModuleSelection>) => {
  const { t } = useTranslation();
  const { showDialog } = useDialog();
  const { reportApiError } = useApiErrorReporter();
  const queryClient = useQueryClient();
  const selectedModule = useAppStateStore((state) => state.selectedModule);

  // Use provided params or fall back to selected module
  const targetModule = params || selectedModule;

  const submitModuleSubmissionMutation = useSubmitModuleSubmission({
    mutation: {
      onMutate: async () => {
        if (!targetModule) {
          showDialog({
            title: t('errors.invalid_state') || 'Invalid State',
            message: t('student.module.errors.no-module-selected')
              || 'Cannot submit module: No module selected. Please select a module first.',
            buttons: [{ text: t('common.buttons.ok') }],
          });
          throw new Error('Cannot submit: no module selected');
        }

        // match this up with the query key of getModules
        const queryKey = getGetModulesQueryKey();

        // Cancel any outgoing refetches to avoid overwriting optimistic update
        await queryClient.cancelQueries({
          queryKey,
        });

        // Snapshot the previous values for potential rollback
        const previousModules = queryClient.getQueryData<AxiosResponse<GenericApiResponseListModule>>(queryKey);

        // Optimistically update the modules query data if it exists
        if (previousModules?.data?.data) {
          queryClient.setQueryData<AxiosResponse<GenericApiResponseListModule>>(queryKey, {
            ...previousModules,
            data: {
              ...previousModules.data,
              data: previousModules.data.data.map((module) => (module.id === targetModule.moduleId
                && module.courses.some((course) => course.id === targetModule.courseId)
                ? {
                  ...module,
                  state: {
                    ...module.state,
                    studentWorkflowState: 'submitted',
                  },
                }
                : module)),
            },
          });
        }

        return { previousModules };
      },
      onError: (
        error: AxiosError<HTTPValidationError>,
        variables,
        context,
      ) => {
        // Report error using reporter with expected codes to suppress Sentry noise
        // PERMISSION_DENIED is expected if user double-submits or cache is stale
        reportApiError(error, 'Module submission', 'toast', {
          expectedErrorCodes: ['PERMISSION_DENIED', 'MODULE_SUBMISSION_INCOMPLETE'],
        });

        // Revert optimistic updates on error
        if (context?.previousModules) {
          queryClient.setQueryData(getGetModulesQueryKey(), context.previousModules);
        }
      },
      onSettled: () => {
        // Always invalidate to ensure cache consistency
        queryClient.invalidateQueries({
          queryKey: getGetModulesQueryKey(),
        });
      },
    },
  });

  return submitModuleSubmissionMutation;
};

export default useSubmitModule;
